import type { SyntheticEvent } from 'react';
import React, { useRef } from 'react';
import { useOnMount } from 'tradera/hooks/use-on-mount';

type LazyImageLoaderProps = {
    src: string;
    alt?: string;
    onLoad?: (event?: SyntheticEvent | Event) => void;
    onError?: (event?: SyntheticEvent | Error) => void;
} & React.ComponentProps<'img'>;

export const LazyImageLoader = ({
    onLoad,
    onError,
    src,
    alt,
    ...rest
}: LazyImageLoaderProps) => {
    const imgElement = useRef<HTMLImageElement>(null);

    useOnMount(() => {
        if (imgElement.current) {
            // An image might load from SSR HTML before JavaScript is fetched, parsed & executed.
            // This prevents an already loaded image from being stuck in an infinite loading state in react in the browser.
            const imageFetchingDone = imgElement.current.complete;
            // That image fetching is done, can either mean an image has loaded or failed to load.
            if (imageFetchingDone) {
                // If the image has an intrinsic width, it has loaded successfully.
                const imageHasLoaded = imgElement.current.naturalWidth > 0;

                if (imageHasLoaded && onLoad) {
                    onLoad(new Event('load'));
                } else if (!imageHasLoaded && onError) {
                    onError(new Error(`Failed to load image ${src}`));
                }
            }
            // This runs once on mount, after that the onLoad/onError callbacks take over if the image has not loaded yet.
        }
    });

    return (
        <img
            ref={imgElement}
            loading="lazy"
            onLoad={onLoad}
            onError={onError}
            src={src}
            alt={alt || ''}
            {...rest}
        />
    );
};
